import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { FileText, LayoutDashboard, Map, CheckSquare, CalendarClock, User, LogOut, Menu, X, Compass, FolderOpen, Clock } from "lucide-react";

const navLinkDefs = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard },
  { to: "/forms", key: "forms", icon: FileText },
  { to: "/roadmaps", key: "roadmaps", icon: Map },
  { to: "/documents", key: "documents", icon: FolderOpen },
  { to: "/timeline", key: "timeline", icon: Clock },
  { to: "/checklists", key: "checklists", icon: CheckSquare },
  { to: "/deadlines", key: "deadlines", icon: CalendarClock },
];

const publicLinkDefs = [
  { to: "/pricing", key: "pricing" },
  { to: "/guides", key: "guides" },
  { to: "/dmv-guide", key: "dmvGuide" },
];

export function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lp = useLocalePath();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = navLinkDefs.map((l) => ({ ...l, label: t(`nav.${l.key}`) }));
  const publicLinks = publicLinkDefs.map((l) => ({ ...l, label: t(`nav.${l.key}`) }));

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className="glass-nav sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : lp("/")} className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
            <Compass className="h-6 w-6 text-teal-700" />
            <span className="font-heading font-bold text-lg text-slate-900">ImmigrationGuide</span>
          </Link>

          {user ? (
            /* Authenticated nav */
            <>
              <div className="hidden lg:flex items-center gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.to} to={link.to}
                    data-testid={`nav-${link.key}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      location.pathname.startsWith(link.to)
                        ? "bg-teal-50 text-teal-700"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <link.icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu-trigger"
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                    >
                      {user.picture ? (
                        <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-sm font-semibold text-teal-700">{user.name?.[0] || "U"}</span>
                        </div>
                      )}
                      <span className="hidden lg:inline text-sm font-medium text-slate-700">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                      <User className="mr-2 h-4 w-4" /> {t("nav.profile")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" /> {t("nav.logOut")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  data-testid="mobile-menu-toggle"
                >
                  {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </>
          ) : (
            /* Public / unauthenticated nav */
            <>
              <div className="hidden md:flex items-center gap-2">
                {publicLinks.map((link) => (
                  <Link key={link.to} to={lp(link.to)} data-testid={`nav-${link.key}`}>
                    <Button variant="ghost" className="text-slate-600 hover:text-slate-900 font-medium text-sm px-3">
                      {link.label}
                    </Button>
                  </Link>
                ))}
                <LanguageSwitcher />
                <Link to={lp("/auth")} data-testid="nav-signin">
                  <Button variant="ghost" className="text-slate-600 hover:text-slate-900 font-medium text-sm px-3">
                    {t("nav.signIn")}
                  </Button>
                </Link>
                <Link to={lp("/auth?mode=register")} data-testid="nav-getstarted">
                  <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium text-sm shadow-sm ml-1">
                    {t("nav.startFree")}
                  </Button>
                </Link>
              </div>

              {/* Mobile: CTA + lang + hamburger */}
              <div className="flex md:hidden items-center gap-1">
                <LanguageSwitcher />
                <Link to={lp("/auth?mode=register")} data-testid="nav-getstarted-mobile">
                  <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-medium text-xs">
                    {t("nav.startFreeShort")}
                  </Button>
                </Link>
                <button
                  className="p-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  data-testid="public-mobile-menu-toggle"
                >
                  {mobileOpen ? <X className="h-5 w-5 text-slate-700" /> : <Menu className="h-5 w-5 text-slate-700" />}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Mobile drawer (authenticated) */}
        {user && mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-slate-100 mt-2 pt-3">
            {navLinks.map((link) => (
              <Link
                key={link.to} to={link.to}
                onClick={closeMobile}
                data-testid={`mobile-nav-${link.key}`}
                className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium ${
                  location.pathname.startsWith(link.to)
                    ? "bg-teal-50 text-teal-700"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <link.icon className="h-4 w-4" />
                {link.label}
              </Link>
            ))}
          </div>
        )}

        {/* Mobile drawer (public / unauthenticated) */}
        {!user && mobileOpen && (
          <div className="md:hidden pb-4 border-t border-slate-100 mt-2 pt-3" data-testid="public-mobile-menu">
            {publicLinks.map((link) => (
              <Link
                key={link.to} to={lp(link.to)}
                onClick={closeMobile}
                className="flex items-center px-3 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                {link.label}
              </Link>
            ))}
            <Link to={lp("/auth")} onClick={closeMobile} className="flex items-center px-3 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">
              {t("nav.signIn")}
            </Link>
            <div className="mt-2 px-3">
              <Link to={lp("/auth?mode=register")} onClick={closeMobile}>
                <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white font-medium text-sm">
                  {t("nav.startFree")}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
